import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, Calendar, MessageSquare, X } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function FloatingCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && !isDismissed && (
        <>
          {/* Mobile Sticky Bottom Bar (Visible on screens < md) */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 25 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-stone-100 p-3 flex gap-3 md:hidden shadow-[0_-8px_30px_rgba(0,0,0,0.08)]"
          >
            <a
              href="tel:+4522458567"
              className="flex-1 flex items-center justify-center gap-2 bg-stone-900 text-white py-3 px-4 rounded-xl font-bold text-sm active:scale-95 transition-transform"
            >
              <Phone className="w-4 h-4 text-orange-500 fill-orange-500" />
              <span>Ring op</span>
            </a>
            <Link
              to="/kontakt"
              className="flex-1 flex items-center justify-center gap-2 bg-orange-600 text-white py-3 px-4 rounded-xl font-bold text-sm active:scale-95 transition-transform shadow-lg shadow-orange-600/20"
            >
              <Mail className="w-4 h-4" />
              <span>Få tilbud</span>
            </Link>
          </motion.div>

          {/* Desktop Floating Panel (Visible on screens >= md) */}
          <motion.div
            initial={{ x: 100, opacity: 0, scale: 0.9 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: 100, opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed bottom-8 right-8 z-50 hidden md:flex flex-col gap-3 items-end"
          >
            {/* Quick Contact Card */}
            <div className="bg-white border border-stone-100 rounded-2xl p-4 shadow-[0_10px_40px_rgba(0,0,0,0.08)] flex flex-col gap-3 w-72 backdrop-blur-md bg-white/95 relative">
              <button
                onClick={() => setIsDismissed(true)}
                className="absolute top-3 right-3 text-stone-400 hover:text-stone-600 p-1 rounded-full hover:bg-stone-50 transition-colors"
                aria-label="Luk"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3 border-b border-stone-50 pb-3 pr-6">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-600">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-stone-900 text-sm">Brug for hjælp?</p>
                  <p className="text-xs text-stone-500">Få et uforpligtende tilbud i dag</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <a
                  href="tel:+4522458567"
                  className="flex items-center justify-between bg-stone-50 hover:bg-stone-100 text-stone-900 py-2.5 px-3.5 rounded-xl font-semibold text-sm transition-all group"
                >
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-orange-600" />
                    <span>Ring direkte</span>
                  </div>
                  <span className="text-xs text-stone-500 group-hover:text-orange-600 transition-colors">+45 22 45 85 67</span>
                </a>

                <Link
                  to="/kontakt"
                  className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white py-2.5 px-4 rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg shadow-orange-600/10 hover:scale-[1.02]"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Book gratis besigtigelse</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
